import { ID } from "@nova/constants"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { activateHotspotPointLayerAction } from "features/entity-caching/flights/slice"
import { activateFlightPath, setBoundingBoxId } from "features/map/layers/slice"
import { GenericRejectionResponseWithReason } from "integrations/redux/types"
import { RootState } from "integrations/redux"
import { setActiveSidePanelModal } from "./slice"
import { dataUpdated } from "@nova/slice-video"
import { SidePanelModalActivation } from "@nova/component-side-panel-tab-container"
import { selectHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { videoData } from "plugins/shared/useUniversalControl"

export const activateHotspotFlight = createAsyncThunk<
    void,
    { flightId?: ID },
    GenericRejectionResponseWithReason
>("entities/flights/activateHotspotFlight", async (payload, thunkApi) => {
    const { flightId } = payload
    const { dispatch } = thunkApi

    dispatch(activateBaseHotspotLayers({ flightId }))
})

export const activateBaseHotspotLayers = createAsyncThunk<
    void,
    { flightId?: ID },
    GenericRejectionResponseWithReason
>("entities/flights/activateHotspotFlight", async (payload, thunkApi) => {
    const { flightId } = payload
    const { dispatch, getState } = thunkApi

    if (!flightId) return
    const state = getState() as RootState

    //turn on hotspots if they exist, otherwise default to just the flightpath
    const hotspots = selectHotspotsByFlightId(flightId)(state)
    if (hotspots.length > 0) {
        dispatch(activateHotspotPointLayerAction({ id: flightId }))
    } else {
        dispatch(activateFlightPath({ flightId }))
    }
})

export const activateSidePanelModal = createAsyncThunk<
    void,
    SidePanelModalActivation | undefined,
    { state: RootState }
>("ui/activateSidePanelModal", async (props, thunkApi) => {
    const state = thunkApi.getState()
    const dispatch = thunkApi.dispatch
    const activeSidePanelModal = state["side-panel-ui-v1"].activeSidePanelModal

    // If active side panel has the same id and type as the params, return.
    if (activeSidePanelModal?.id === props?.id && activeSidePanelModal?.type === props?.type) return

    // Reset video data.
    videoData.metadata = []
    videoData.colordata = new ArrayBuffer(0)
    dispatch(dataUpdated())

    // Activate the new side panel modal.
    dispatch(
        setActiveSidePanelModal({
            id: props?.id,
            type: props?.type,
            layerContentType: props?.layerContentType,
        })
    )
})

/**
 * Closes the active layer and resets media metadata.
 * Use this on X buttons, etc.
 *
 * Add any "layer" cleanup here.
 */
export const closeLayer = createAsyncThunk<void, undefined, { state: RootState }>(
    "ui/activateSidePanelModal",
    async (_, thunkApi) => {
        const dispatch = thunkApi.dispatch

        // Close any open side panel modal.
        dispatch(setActiveSidePanelModal(undefined))

        // De-select any bounding box.
        dispatch(setBoundingBoxId({ id: undefined }))

        // Reset video data.
        videoData.metadata = []
        videoData.colordata = new ArrayBuffer(0)
        dispatch(dataUpdated())
    }
)
